import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import type { Editor } from '@tiptap/core';
import { useAppState } from '../../app/state';
import { useAppStore } from '../../store';
import { callAction } from '../../app/actions';
import { ApiError, apiRequest } from '../../api/client';
import { DocPill } from '../../components/DocPill';
import { FavStar } from '../../components/FavStar';
import { ReslugButton } from '../../components/ReslugButton';
import { LanguagePicker } from '../../components/LanguagePicker';
import { inEditMode } from '../../lib/mode';
import { WatchToggle } from '../feed/WatchToggle';
import { DocOwnershipCluster } from './DocOwnershipCluster';
import { Avatar } from '../../components/Avatar';
import type { PresenceUser } from '../presence/PresenceStack';
import { DocLogTimeline } from '../doclog/DocLogTimeline';
import { DocEditorPane, type LoadedDoc } from './DocEditorPane';
import { useDocNavLayout } from './useDocNavLayout';
import { presenceText, type PresenceKey } from '../../i18n/presence';
import { nodeTint, ROOT_GRADIENT, toHex6 } from '../../lib/color';
import {
  DOC_BEAN_COLOR,
  DOC_BEAN_ORDER,
  DOC_ID_FIELD_BY_KEY,
  DOC_KINDS,
  docKindForKey,
  docTypeOf,
} from '../../lib/docs';
import { buildGraph } from '../../lib/graph';
import {
  buildHiddenSets,
  canDocBtn,
  canEditAllDocs,
  canEditMap,
  canEnterEdit,
  isPermHidden,
} from '../../lib/permissions';
import { canEditDoc } from '../../lib/responsibility';
import { suggestDocSlug } from '../../lib/slug';
import {
  OWED_CUSTOM_LABEL,
  OWED_PILL_LABEL,
  isExemptPlan,
  plannedMissing,
} from '../../lib/health';
import type { DocId, DocKindKey, DocPerm, DocType, MapNode, NodeId } from '../../lib/types';
import { DOCNAV_STORE } from '../../store/persistence';

/**
 * The documentation overlay: the bar, the navigation band around the page, the page itself in
 * each of its three shapes, and the Tiptap editor.
 *
 * ONE REGION, not the three the plan first listed. `fillDocPill()` is what made the pill
 * inseparable from the map (see `components/DocPill.tsx`; it has since been deleted outright);
 * what makes these three inseparable from each other is simpler — the band, the page and the
 * log timeline are cells of one CSS grid, and the band's geometry is measured from the page's
 * own rectangle.
 *
 * ══ THE MEASURED LAYOUT, WHICH IS THE REHEARSAL FOR THE MAP ══
 *
 * The band is the map's layout circularity in miniature, which is why the plan asks for it
 * first. The vanilla version wrote to the DOM and read it back on the same frame, three times
 * over: `layoutDocNav()` set the children column's margin to zero, measured, and wrote the
 * answer back; `updateParentDocClearance()` wrote a CSS custom property from a measured
 * overlap; `drawDocNavWires()` re-measured everything against the stage and assembled an SVG
 * string. Here measurement is an INPUT — `useDocNavLayout` reads rectangles, `lib/docnav.ts`
 * turns them into an offset, a clearance and a list of paths, and React renders all three.
 * The reset pass is gone: each measurement reports the offset already applied, so the
 * correction is absolute rather than relative. **Do not reintroduce measurement inside layout.**
 *
 * ══ WHAT STAYED ON THE VANILLA SIDE, AND WHY ══
 *
 * "Which page is open, in which mode, from which node" is read by things that are still the
 * vanilla script's: `myPresenceLoc()` reports it to the presence socket, `refreshOpenViews()`
 * re-opens it after a language change, and the map's doc beans and node popup are the callers
 * that set it. So that state stays there and this region renders from it through the bridge —
 * the same arrangement every other ported region has with map state. Writes go back through
 * named actions; a region never mutates the snapshot.
 *
 * ══ THE EDITOR IS A CONTROLLED FORM NOW ══
 *
 * See `DocEditorPane`. The title, the URL and the chosen type are state here; the content comes
 * from the editor instance's `getJSON()`. Save reads those and nothing else. The vanilla save
 * read all four back out of the DOM, which the original research called the most likely source
 * of silent data-loss regressions in the app — and it was the last one left.
 *
 * ══ SMALLER THINGS WORTH KNOWING ══
 *
 * - THE MODE YOU ARE IN travels with you — and since V0.53.5 there is only one to be in. The
 *   bar's Edit switch is the map's toggle (`toggleEditing`); the editor, the create pills and
 *   the ownership box are all DERIVED from that one flag and the scope (`lib/mode.ts`). Before,
 *   the switch flipped a second flag the box did not read, so a page opened from a view-mode
 *   map could not show its workflow chip at all.
 * - A NODE WITH NOTHING says so, rather than leaving the previous page on screen where it read
 *   as this node's documentation. The wording changes for a node EXEMPT in its plan (V0.42):
 *   "needs none" and "missing" are different answers to the same question.
 * - A CROSS-ORIGIN FRAME's `contentDocument` is `null` in Chrome rather than a throw, so only a
 *   document we can READ and which is genuinely blank counts as a failed load.
 */

/* Session-scoped, like the vanilla band: expanded by default, remembered per tab. The key
   constant lives in store/persistence.ts — the one file that names every storage key — and is
   imported from there so that claim stays import-enforced. */

/**
 * How many of a PARENT's pills show before the rest collapse into "+N more".
 *
 * Three plus that pill fills the two-column block exactly, which is what keeps the block two
 * rows tall and the log panel below it unobstructed.
 */
const PARENT_DOC_CAP = 3;

/** Custom pages shown in an uncapped column before they collapse. */
const CHILD_CUSTOM_SHOWN = 2;

/** Avatars in the bar before they collapse into "+N". */
const DOC_PRESENCE_MAX = 4;

/** How long "Saved ✓" stays on the button. */
const SAVED_FLASH_MS = 1200;

/** The vanilla editor's check, kept exactly: a target has to be a real http(s) URL. */
function isHttpUrl(u: string): boolean {
  const v = String(u || '').toLowerCase();
  return v.startsWith('http://') || v.startsWith('https://');
}

const kindOf = (key: DocKindKey) => DOC_KINDS.find((d) => d.key === key)!;
const slotDocId = (n: MapNode, key: DocKindKey): DocId | null =>
  (n[DOC_ID_FIELD_BY_KEY[key]] as DocId | null | undefined) ?? null;

/** Does this node point at the page in any of its slots? */
function ownsDoc(n: MapNode, id: DocId): boolean {
  return (
    n.docId === id ||
    n.internalDocId === id ||
    n.courseDocId === id ||
    n.demoDocId === id ||
    (n.customDocs ?? []).includes(id)
  );
}

/** One entry in a doc column: a fixed slot, or a custom page that has no slot at all. */
interface DocEntry {
  key: DocKindKey | null;
  docId: DocId;
}

/**
 * What the band's leaf components need, gathered once.
 *
 * They are declared at module scope rather than inside the region, so their identity is stable
 * and React updates the band instead of tearing it down and rebuilding it on every keystroke in
 * the title field. This bundle is what they would otherwise close over.
 */
interface BandCtx {
  light: boolean;
  /** Where a node is when it is not on the map (V0.47) — the bubble draws empty until the
   *  node has landed in it, and empties again as it leaves. See `perch` in the ui slice. */
  perch: { id: NodeId; at: 'seat' | 'bubble' | 'air' } | null;
  /** The node the band is centred on — the one drawn `active`. */
  focusId: NodeId | null;
  /** The page on screen, so its pill can invert. */
  openDocId: DocId | null;
  docTypes: Record<DocId, DocType>;
  expanded: boolean;
  entries: (n: MapNode, cap?: number) => { shown: DocEntry[]; rest: number };
  labelOf: (e: DocEntry) => string;
  colorOf: (e: DocEntry) => string;
  onToggleExpanded: () => void;
  onOpenNode: (id: NodeId) => void;
  onOpenPopup: (id: NodeId) => void;
  onOpenEntry: (owner: MapNode, e: DocEntry) => void;
}

/** A static node bubble (parent / current / child): map-styled, minus the map's chrome. */
function NavNode({
  n,
  role,
  ctx,
}: {
  n: MapNode;
  role: 'parent' | 'current' | 'child';
  ctx: BandCtx;
}) {
  const c = toHex6(n.color || '#5b8cff');
  // Painted by the same formula as the map node and the popup's seat (lib/color.ts), because
  // the node FLIES between those three boxes (V0.47) and a shade's difference would show as it
  // landed.
  const style: CSSProperties = n.root
    ? { background: ROOT_GRADIENT, color: '#fff' }
    : nodeTint(c, ctx.light);
  const classes = ['docnav-node'];
  if (n.root) classes.push('root');
  if (role === 'current') classes.push('big');
  if (n.id === ctx.focusId) classes.push('active');
  // The current bubble is the node's DESTINATION when a page opens and its DEPARTURE when it
  // closes: while the node is in the AIR, the box is drawn and its paint is not. Only the air
  // — a page opened without a flight (the doc picker, a deep link) must still show its bubble
  // even though the node is, as far as the perch knows, sitting in a popup's seat beneath it.
  if (role === 'current' && ctx.perch && ctx.perch.id === n.id && ctx.perch.at === 'air') {
    classes.push('away');
  }

  return (
    <div
      className={classes.join(' ')}
      data-id={n.id}
      style={style}
      onClick={() => ctx.onOpenNode(n.id)}
    >
      {n.root ? null : <span className="swatch" style={{ background: c }} />}
      <span className="txt">{n.label || ''}</span>
      {role === 'current' ? (
        <button
          type="button"
          className="docnav-toggle"
          title={ctx.expanded ? 'Collapse children' : 'Expand children'}
          onClick={(e) => {
            e.stopPropagation();
            ctx.onToggleExpanded();
          }}
        >
          {ctx.expanded ? '–' : '+'}
        </button>
      ) : null}
    </div>
  );
}

/** One pill in the band. Inverted when it is the page currently on screen. */
function NavPill({ owner, entry, ctx }: { owner: MapNode; entry: DocEntry; ctx: BandCtx }) {
  return (
    <DocPill
      variant="nav"
      label={ctx.labelOf(entry)}
      color={ctx.colorOf(entry)}
      docId={entry.docId}
      docTypes={ctx.docTypes}
      active={entry.docId === ctx.openDocId}
      onClick={(e) => {
        e.stopPropagation();
        ctx.onOpenEntry(owner, entry);
      }}
    />
  );
}

/** A column of pills under a parent or child bubble. */
function DocCol({ n, cap, ctx }: { n: MapNode; cap?: number; ctx: BandCtx }) {
  const { shown, rest } = ctx.entries(n, cap);
  return (
    <div className="docnav-doccol">
      {shown.map((e) => (
        <NavPill key={e.docId} owner={n} entry={e} ctx={ctx} />
      ))}
      {rest > 0 ? (
        <DocPill
          variant="nav"
          label={`+${rest} more`}
          color="var(--ink-faint)"
          icon={null}
          onClick={(e) => {
            e.stopPropagation();
            // From the PARENT slot the useful move is to GO there: it becomes the current node
            // and shows every one of its pages in full. Elsewhere, the node's popup.
            if (cap) ctx.onOpenNode(n.id);
            else ctx.onOpenPopup(n.id);
          }}
        />
      ) : null}
    </div>
  );
}

export function DocOverlay({ host }: { host: HTMLElement }) {
  const s = useAppState();
  // Store-only, not on the snapshot: where the node is while it flies (V0.47). Read here so
  // the current bubble can draw itself empty until the node has landed in it.
  const perch = useAppStore((st) => st.perch);

  const open = s.docOpen;
  const docId = s.docCurrentId;
  const navId = s.docNavNodeId;
  const focus = navId ? (s.nodes[navId] ?? null) : null;
  const type = docTypeOf(s.docTypes, docId);
  const noDoc = !docId && !s.docCurrent;
  /* The V0.37 gates, each precise: PARTICIPANTS see the review chip (read-only without the
     verbs — the data arrives for exactly them); editing THIS page is `canEditDoc` over the
     current locale (the server re-checks against the resolved one on every write); creating
     and attaching pages is EDIT_MAP — creation from the band always wires a slot, which is a
     nodes-family write, so the stricter brick is the honest gate for the create pills. */
  const participant = canEnterEdit(s.perm);
  const mayEditThisDoc = docId ? canEditDoc(s, docId, s.currentLocale) : false;
  const canCreateDocs = canEditMap(s.perm);
  const mayDeleteDoc = canEditMap(s.perm) || canEditAllDocs(s.perm);
  /* ONE EDIT MODE (V0.53.5). `editing` is the app's one flag — the bar's switch and the map's
     toggle are the same control. The doc view's EDIT SHAPE is derived from it and the scope:
     on a page, the editor, when this user may edit it here (`docEditorActive` in lib/mode.ts,
     spelled out inline because `mayEditThisDoc` is needed below on its own); on a bare node,
     the create pills, for EDIT_MAP (`docCreateShape`). A participant in edit mode on a page
     that is not theirs gets the reader with the working chrome — the box, the switch pressed. */
  const editing = inEditMode(s);
  const editMode = editing && (docId ? mayEditThisDoc : canCreateDocs);
  /** Edit mode has two shapes; only "editing an actual page" gets the editor chrome. */
  const editingDoc = editMode && !!docId;
  const mode: 'view' | 'edit' = editMode ? 'edit' : 'view';

  const docBtn = useCallback((p: DocPerm) => canDocBtn(s.perm, p), [s.perm]);
  const hidden = useMemo(() => buildHiddenSets(s.perm, s.tagEdges), [s.perm, s.tagEdges]);
  const graph = useMemo(() => buildGraph(s.edges), [s.edges]);

  const stageRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const editorRef = useRef<Editor | null>(null);

  const [expanded, setExpandedState] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(DOCNAV_STORE) !== '0';
    } catch {
      return true;
    }
  });
  const toggleExpanded = useCallback(() => {
    setExpandedState((prev) => {
      try {
        sessionStorage.setItem(DOCNAV_STORE, prev ? '0' : '1');
      } catch {
        /* private mode: the preference simply does not persist */
      }
      return !prev;
    });
  }, []);

  const [logsVisible, setLogsVisible] = useState(false);
  const [failedLoad, setFailedLoad] = useState(false);

  // The editor's tracked state. Nothing here is ever read back out of the DOM.
  const [doc, setDoc] = useState<LoadedDoc | null>(null);
  const [loadMessage, setLoadMessage] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [docType, setDocType] = useState<DocType>('standard');
  const [url, setUrl] = useState('');
  const [saved, setSaved] = useState(false);

  /* ---------- the overlay's own visibility, carried by the host element ---------- */
  useEffect(() => {
    host.classList.toggle('show', open);
  }, [open, host]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      // `{ frame: true }` — the reader's own close puts the map back on this page's node.
      if (e.key === 'Escape') callAction('closeDoc', { frame: true });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  /* ---------- the page, in view mode ---------- */

  // An external link embeds nothing, so there is no frame load to wait on and no error timer
  // to arm — its button is the whole page.
  const showFrame = mode === 'view' && !!s.docCurrent && type !== 'external';
  const frameSrc = showFrame ? s.docCurrent : 'about:blank';
  const loaded = useRef(false);

  useEffect(() => {
    setFailedLoad(false);
    loaded.current = false;
    if (!showFrame) return;
    // An embedded page is somebody else's site and may be slow; a page we render ourselves is
    // either there or it is not.
    const failAfter = type === 'iframe' ? 12_000 : 1_500;
    const timer = window.setTimeout(() => {
      if (!loaded.current) setFailedLoad(true);
    }, failAfter);
    return () => window.clearTimeout(timer);
    // docOpenSeq re-arms the watch when the SAME page is opened again.
  }, [showFrame, frameSrc, type, s.docOpenSeq]);

  const onFrameLoad = () => {
    if (!showFrame) return; // a blanked frame is not a document that failed
    loaded.current = true;
    // A cross-origin document is unreadable — null in Chrome, a throw elsewhere. Either way
    // `load` firing means the browser rendered something, which is success.
    let d: Document | null = null;
    try {
      d = frameRef.current?.contentDocument ?? null;
    } catch {
      return;
    }
    if (d?.body && d.body.innerHTML.trim() === '' && d.location.href === 'about:blank') {
      setFailedLoad(true);
    }
  };

  /* ---------- the editor ---------- */
  useEffect(() => {
    if (!editingDoc || !docId) {
      setDoc(null);
      setLoadMessage(null);
      return;
    }
    let live = true;
    setDoc(null);
    setLoadMessage(null);
    apiRequest<LoadedDoc>(`/api/docs/${docId}?lang=${s.currentLocale}`).then(
      (d) => {
        if (!live) return;
        setDoc(d);
        setTitle(d.title || '');
        setDocType(d.docType || 'standard');
        setUrl(d.sourceUrl || '');
        // The map's caches learn this page's type and URL, so docUrlFor() builds the right
        // link for it; the bar's title matches what was actually loaded.
        callAction('noteDocMeta', d.id, d.docType, d.sourceUrl);
        callAction('setDocCurrentTitle', d.title);
      },
      (e: Error) => {
        if (!live) return;
        console.error('Failed to load doc editor:', e);
        setLoadMessage(
          'Could not load the editor (see browser console for details). Check your connection and try again.',
        );
      },
    );
    return () => {
      live = false;
    };
    // docOpenSeq is what reloads after a revision restore, where nothing else changed.
  }, [editingDoc, docId, s.currentLocale, s.docOpenSeq]);

  /* ---------- the sitting (V0.53.5) ---------- */
  // The editor is DERIVED from the one flag now, so nobody mints its session id at "open";
  // the mount is the sitting. Same dependencies as the fetch above, deliberately: a restore
  // (the sequence), a language switch and a re-entry through the switch each start a new
  // entry in the history, exactly as `openDocEditor` used to by being called again.
  useEffect(() => {
    if (!editingDoc || !docId) return;
    callAction('beginDocEditSession');
    return () => {
      callAction('endDocEditSession');
    };
  }, [editingDoc, docId, s.currentLocale, s.docOpenSeq]);

  /**
   * Save the title (and, for a standard page, the content) of one translation.
   *
   * THE RESPONSE IS THE ROW AFTER THE AUTOMATIC DEMOTION. A content-changing write resets
   * that page+locale to `draft`, server-side inside `recordRevision`, and the route re-reads
   * the row so the answer carries the workflow state as it now stands. Handing it straight to
   * `noteDocWorkflow` is what makes the doc bar's chip fall back to Draft with NO REFETCH and
   * no second source of truth — the alternative, asking again after every save, would be a
   * request whose answer we were already holding.
   */
  const saveTranslation = async (id: DocId, t: string, contentJson?: unknown) => {
    const row = await apiRequest<{
      locale?: string;
      workflowState?: string;
      reviewedAt?: string | null;
      /** V0.45.1: the address's mark, which the map would not bring back after our own write. */
      slugDrifted?: boolean;
    }>(
      `/api/docs/${id}/translations/${s.currentLocale}`,
      {
        method: 'PUT',
        body: {
          title: t,
          contentJson,
          // Constant for one open-edit-save-save-close cycle, so repeated saves collapse into
          // a single log entry instead of one per click.
          editSessionId: s.docEditSessionId ?? undefined,
        },
      },
    );
    callAction('noteDocWorkflow', id, row?.locale || s.currentLocale, row);
    return row;
  };

  const save = async () => {
    if (!docId) return;
    if (docType === 'standard' && !editorRef.current) return;
    const t = title.trim() || 'Untitled';
    try {
      // The type patch goes LAST on purpose. With it first, a failing translation save left
      // the type already changed on the server while the user was told the save failed — the
      // page then came back as the new type on the next open, which read as a phantom save.
      if (docType !== 'standard') {
        const target = url.trim();
        if (!isHttpUrl(target)) {
          alert('The URL must start with http:// or https://');
          return;
        }
        await saveTranslation(docId, t); // title only — there is no content
        await apiRequest(`/api/docs/${docId}`, {
          method: 'PATCH',
          body: { docType, sourceUrl: target },
        });
        callAction('noteDocMeta', docId, docType, target);
      } else {
        await saveTranslation(docId, t, editorRef.current?.getJSON());
        // Always patch, so switching a page back to 'standard' is persisted even when the
        // written content itself has not changed.
        await apiRequest(`/api/docs/${docId}`, {
          method: 'PATCH',
          body: { docType: 'standard', sourceUrl: null },
        });
        callAction('noteDocMeta', docId, 'standard', null);
      }
      callAction('afterDocSaved', docId, t);
      setSaved(true);
      window.setTimeout(() => setSaved(false), SAVED_FLASH_MS);
    } catch (e) {
      // A page too big for the server's body cap is not a fault anyone can retry past, and
      // the generic line read as a connection problem. Say what it is; nothing was saved.
      if (e instanceof ApiError && e.status === 413) {
        const limit = (e.body as { limit?: string } | undefined)?.limit;
        alert(`This page is too large to save${limit ? ` (the limit is ${limit.toUpperCase()})` : ''}. Nothing was saved.`);
        return;
      }
      alert('Could not save the doc page.');
    }
  };

  const remove = async () => {
    if (!docId) return;
    const name = s.docTitles[docId] || 'this document';
    if (
      !confirm(
        `Delete “${name}”? This permanently removes the document and detaches it from every node.`,
      )
    ) {
      return;
    }
    try {
      await apiRequest(`/api/docs/${docId}`, { method: 'DELETE' });
      // The server unlinks the page from every node; mirror that locally, then close.
      callAction('forgetDoc', docId);
    } catch {
      alert('Could not delete the document.');
    }
  };

  /* ---------- the navigation band ---------- */

  const wireColorOf = useCallback(
    (id: string | null) => {
      const n = id ? s.nodes[id] : null;
      return n && !n.root ? toHex6(n.color || '#8b6bff') : '#5b8cff';
    },
    [s.nodes],
  );

  const parent = useMemo(() => {
    if (!focus) return null;
    const p = s.nodes[s.edges[focus.id]];
    return p && !isPermHidden(p.id, s.edges, hidden) ? p : null;
  }, [focus, s.nodes, s.edges, hidden]);

  const kids = useMemo(() => {
    if (!focus || !expanded) return [] as NodeId[];
    return graph
      .childrenOf(focus.id)
      .filter((id) => !isPermHidden(id, s.edges, hidden))
      // Top to bottom by canonical y, so the band reads the way the map does.
      .sort((a, b) => (s.nodes[a]?.y ?? 0) - (s.nodes[b]?.y ?? 0));
  }, [focus, expanded, graph, s.edges, s.nodes, hidden]);

  const navKey = [focus?.id ?? '', parent?.id ?? '', kids.join(','), mode, docId ?? '', logsVisible].join(
    '|',
  );

  const nav = useDocNavLayout({
    stage: stageRef,
    grid: gridRef,
    active: !!focus,
    currentId: focus?.id ?? null,
    colorOf: wireColorOf,
    navKey,
  });

  const openEntry = useCallback(
    (owner: MapNode, e: DocEntry) => {
      const label = s.docTitles[e.docId] || (e.key ? kindOf(e.key).label : 'Untitled');
      callAction(
        'openDocInCurrentMode',
        e.docId,
        (owner.label ? `${owner.label} — ` : '') + label,
        owner.id,
      );
    },
    [s.docTitles],
  );

  const ctx: BandCtx = useMemo(
    () => ({
      light: s.isLight,
      perch,
      focusId: navId,
      openDocId: docId,
      docTypes: s.docTypes,
      expanded,
      /** Every page on a node, in display order, split into what fits and what does not. */
      entries: (n, cap) => {
        const fixed: DocEntry[] = DOC_BEAN_ORDER.filter(
          (key) => canDocBtn(s.perm, kindOf(key).perm) && slotDocId(n, key),
        ).map((key) => ({ key, docId: slotDocId(n, key)! }));
        const customs: DocEntry[] = (canDocBtn(s.perm, 'custom') ? (n.customDocs ?? []) : []).map(
          (id) => ({ key: null, docId: id }),
        );
        const all = [...fixed, ...customs];
        // `cap` bounds the WHOLE list and belongs to the parent slot alone, because that block
        // floats out of flow into the log panel's row. Uncapped keeps the original rule: every
        // fixed slot plus two custom pages, since that column scrolls and can afford length.
        const shown = cap ? all.slice(0, cap) : [...fixed, ...customs.slice(0, CHILD_CUSTOM_SHOWN)];
        return { shown, rest: all.length - shown.length };
      },
      labelOf: (e) => s.docTitles[e.docId] || (e.key ? kindOf(e.key).label : 'Untitled'),
      colorOf: (e) => (e.key ? DOC_BEAN_COLOR[e.key] : 'var(--ink-faint)'),
      onToggleExpanded: toggleExpanded,
      onOpenNode: (id) => callAction('navOpenNode', id),
      onOpenPopup: (id) => callAction('openPopup', id),
      onOpenEntry: openEntry,
    }),
    [s.isLight, perch, s.docTypes, s.docTitles, s.perm, navId, docId, expanded, toggleExpanded, openEntry],
  );

  const createDoc = async (owner: MapNode, key: DocKindKey | 'custom') => {
    const kindLabel = key === 'custom' ? 'Custom' : kindOf(key).label;
    const t = (owner.label ? `${owner.label} — ` : '') + kindLabel;
    try {
      const created = await apiRequest<{ id: string }>('/api/docs', {
        method: 'POST',
        body: {
          // A SUGGESTION. The server generates the real slug — it folds diacritics and
          // resolves collisions — and taking a client slug verbatim is what made two documents
          // with the same title a 500.
          slug: suggestDocSlug(t),
          kind: docKindForKey(key),
          locale: s.currentLocale,
          title: t,
          docType: 'standard',
        },
      });
      // Attaching is a change to the NODE, so the vanilla side does it.
      callAction('attachDocToTarget', owner.id, key, created.id, t);
      callAction('noteDocTitle', created.id, t);
      callAction('openDocEditor', created.id, owner.id);
    } catch {
      alert('Could not create the document.');
    }
  };

  /* The current node's pills, split above and below the connector so the bubble keeps its
     spine. In edit mode every permitted slot shows — a missing one as a "create" pill. */
  let currentDocs: ReactNode = null;
  /* Whether the band actually offers a create pill. The stand-in's wording used to promise
     "the outlined buttons above" unconditionally, which stopped being true the moment the band
     narrowed to what the PLAN expects (V0.42.5) — an unplanned node has no pills to point at. */
  let bandOffersCreate = false;
  if (focus) {
    const baseKeys = DOC_BEAN_ORDER.filter(
      (k) => docBtn(kindOf(k).perm) && (editMode || slotDocId(focus, k)),
    );
    const customs = docBtn('custom') ? (focus.customDocs ?? []) : [];
    /* What the node's PLAN owes but has not attached (V0.42.1) — the band's share of the
       dotted-pill treatment the map and the popup already give. An owed slot draws in the
       KIND'S colour instead of the neutral "+", so "this is missing and somebody promised it"
       reads differently from "this slot happens to be empty". Same click either way. */
    const owed = plannedMissing(focus, docBtn);
    const owedKinds = new Set<string>(owed.kinds);
    bandOffersCreate =
      editMode && canCreateDocs && (owed.kinds.length > 0 || owed.customMissing > 0);
    const slotPill = (k: DocKindKey): ReactNode => {
      const id = slotDocId(focus, k);
      if (id) return <NavPill key={k} owner={focus} entry={{ key: k, docId: id }} ctx={ctx} />;
      if (!editMode || !canCreateDocs) return null;
      // ONLY WHAT THE PLAN EXPECTS (V0.42.5). The band used to offer a neutral "+ Kind" for
      // every empty slot, which said "you could put something here" — a different statement
      // from "this node owes one", and the only one of the three doc surfaces still making it.
      // A slot nobody planned is now silent here; attaching one is the node panel's job.
      if (!owedKinds.has(kindOf(k).perm)) return null;
      return (
        <DocPill
          key={k}
          variant="nav"
          planned
          color={DOC_BEAN_COLOR[k]}
          label={OWED_PILL_LABEL(kindOf(k).label)}
          title={`${kindOf(k).label} — planned, not yet created. Click to create it.`}
          onClick={(e) => {
            e.stopPropagation();
            void createDoc(focus, k);
          }}
        />
      );
    };
    currentDocs = (
      <div className="docnav-currentdocs">
        <div className="docnav-pillrow">
          {baseKeys.filter((k) => k === 'link' || k === 'linkCourse').map(slotPill)}
          {customs
            .filter((_, i) => i % 2 === 0)
            .map((id) => (
              <NavPill key={id} owner={focus} entry={{ key: null, docId: id }} ctx={ctx} />
            ))}
        </div>
        <div className="docnav-pillrow">
          {baseKeys.filter((k) => k === 'linkDemo' || k === 'linkInternal').map(slotPill)}
          {customs
            .filter((_, i) => i % 2 === 1)
            .map((id) => (
              <NavPill key={id} owner={focus} entry={{ key: null, docId: id }} ctx={ctx} />
            ))}
          {/* Custom documents the plan still OWES, one dotted pill each (V0.42.1). */}
          {editMode && canCreateDocs && docBtn('custom')
            ? Array.from({ length: owed.customMissing }, (_, i) => (
                <DocPill
                  key={`owed-custom-${i}`}
                  variant="nav"
                  planned
                  color="var(--ink-faint)"
                  label={OWED_PILL_LABEL(OWED_CUSTOM_LABEL)}
                  title="Custom document — planned, not yet created. Click to create it."
                  onClick={(e) => {
                    e.stopPropagation();
                    void createDoc(focus, 'custom');
                  }}
                />
              ))
            : null}
        </div>
      </div>
    );
  }

  /* ---------- who else is on this page ---------- */
  const myId = s.currentUser?.id;
  const alsoHere = docId
    ? s.presenceRoster.filter((u) => u?.loc?.docId === docId && u.userId !== myId)
    : [];
  const activityOf = (u: PresenceUser): string => {
    const t = (k: PresenceKey) => presenceText(k, s.currentLocale);
    const verb = u.loc?.mode === 'edit' ? t('editing') : t('viewing');
    const name = u.loc?.docId ? s.docTitles[u.loc.docId] : '';
    return `${verb} ${name ? `“${name}”` : t('adoc')}`;
  };

  /* ---------- the page cell, in view mode ---------- */

  // The node this page belongs to: the one it was opened from, else the first that carries it.
  // Read once for the pill colour below and for "Show in map" in the bar.
  const ownerNode: MapNode | null =
    focus ?? (docId ? (Object.values(s.nodes).find((x) => ownsDoc(x, docId)) ?? null) : null);

  // The kind colour of a page, taken from the slot it occupies on its owner node — the same
  // colour its pill has on the map. A custom page has no slot, so it takes the neutral one.
  const docColor = ((): string => {
    if (ownerNode && docId) {
      for (const k of DOC_KINDS) if (slotDocId(ownerNode, k.key) === docId) return DOC_BEAN_COLOR[k.key];
    }
    return 'var(--ink-faint)';
  })();

  // Opened from the dashboard's Documents tab: it is still there beneath us, and Close returns to it.
  const overDocuments = s.adminScreen === 'documents';

  const showActions = !editingDoc && !noDoc && mode === 'view' && type !== 'standard';
  const showStandin = noDoc || (mode === 'view' && type === 'external');

  return (
    <>
      {/* THE BAR MIRRORS THE MAP'S (V0.44.1): three zones, the top bar's shape. Left, the edit
          controls where the map's Edit toggle sits; centre, the title where the map's search box
          sits; right, the ownership box, presence, the ONE language selector (the same control in
          both modes, always visible, outside the box), "Show in map", "Open in new tab", Close.
          Every id is unchanged — the suite drives the bar by id, never by position. */}
      <div className="docbar">
        <div className="docbar-left">
          {/* THE MAP'S EDIT TOGGLE, in this bar (V0.53.5): the same gate (`canEnterEdit`), the
              same flag, the same action — `toggleEditing`, which saves the preference and
              badges, as the top bar's does. What the mode SHOWS on this page is derived above:
              the editor for a page this user may edit, the create pills on a bare node, the
              reader with the working chrome otherwise — hence the title in that last case. */}
          {participant && (docId || navId) ? (
            <button
              className={editing ? 'btn editswitch on' : 'btn editswitch'}
              id="doc-editswitch"
              aria-pressed={editing ? 'true' : 'false'}
              title={
                editing && docId && !mayEditThisDoc
                  ? 'Edit mode is on. This page is not yours to edit in this language.'
                  : undefined
              }
              onClick={() => callAction('toggleEditing')}
            >
              <span className="dot-ind" />
              <svg className="ico">
                <use href="#i-edit" />
              </svg>
              <span id="doc-edit-label">{editing ? 'Editing' : 'View only'}</span>
            </button>
          ) : null}
          {editingDoc ? (
            <button className="btn primary" id="doc-save" onClick={() => void save()}>
              <span>{saved ? 'Saved ✓' : 'Save'}</span>
            </button>
          ) : null}
          {/* Deletion detaches from every node — EDIT_MAP or the admin override, never a doc
              responsible's: they edit and clear, they do not remove the page from the map. */}
          {editingDoc && mayDeleteDoc ? (
            <button
              className="btn danger icon-only"
              id="doc-delete"
              title="Delete this document"
              aria-label="Delete this document"
              onClick={() => void remove()}
            >
              <svg className="ico">
                <use href="#i-trash" />
              </svg>
            </button>
          ) : null}
        </div>

        <div className="docbar-center">
          <span
            className="doctitle"
            id="doc-title"
            style={{ display: editingDoc ? 'none' : undefined }}
          >
            {s.docCurrentTitle}
          </span>
          <input
            className="fld doctitle-input"
            id="doc-title-input"
            placeholder="Document title…"
            style={{ display: editingDoc ? undefined : 'none' }}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          {/* The favorite star (plan 2.4) and the watch bell (plan Phase 3), beside the title
              in both modes — starring or watching a page while editing it is as natural as
              while reading it. Star first, bell second, the node popup's order. */}
          {docId ? <FavStar targetType="doc" targetId={docId} id="doc-fav" /> : null}
          {docId ? <WatchToggle targetType="doc" targetId={docId} id="doc-watch" /> : null}
          {/* Star, bell, re-slug — the node popup's order. Edit-mode-gated inside the
              component, which is what keeps `doc-overlay-standard.png` (shot in VIEW mode)
              from moving. */}
          {docId ? (
            <ReslugButton
              kind="doc"
              targetId={docId}
              drifted={!!s.docSlugDrift[docId]}
              id="doc-reslug"
            />
          ) : null}
        </div>

        <div className="docbar-right">
          {/* The OWNERSHIP BOX (V0.42): responsibles · workflow state, one workflow unit.
              PARTICIPANTS, and since V0.44.1 only in EDIT MODE — the box is the working view; a
              reader in View mode gets the page and nothing to act on. Since V0.53.5 that is the
              ONE mode, which the switch at the left of this bar flips: before, it read the map's
              toggle while the switch flipped a second flag, so a page opened from a view-mode
              map could not show its chip at all. The language moved out of the box to the
              shared selector on the right (V0.44.1). */}
          {participant && docId && editing ? (
            <DocOwnershipCluster
              key={docId}
              docId={docId}
              // In edit mode the overlay has already fetched this page for the editor, so the
              // workflow chip adopts that answer instead of asking again — `undefined` in view
              // mode means "nobody is fetching, do it yourself". `e2e/34-doc-overlay.spec.ts`
              // counts those requests and requires exactly one per open.
              seed={editingDoc ? doc : undefined}
            />
          ) : null}
          <div className="doc-presence" id="doc-presence">
            {alsoHere.slice(0, DOC_PRESENCE_MAX).map((u, i) => (
              <Avatar
                key={`${u.userId}-${i}`}
                user={u}
                editing={u.loc?.mode === 'edit'}
                title={`${u.displayName || ''} · ${activityOf(u)}`}
              />
            ))}
            {alsoHere.length > DOC_PRESENCE_MAX ? (
              <span className="avatar more">+{alsoHere.length - DOC_PRESENCE_MAX}</span>
            ) : null}
          </div>
          {/* THE ONE LANGUAGE SELECTOR — the map's `#lang-select`, in this bar: the shared
              `LanguagePicker` (V0.53.5), controlled from the snapshot like every other picker.
              Choosing here changes the language you READ, the workflow you act on and the map
              behind the overlay — ONE current language (V0.43.2/3). */}
          <LanguagePicker id="doc-lang" title="Language" ariaLabel="Language" />
          {/* "Show in map" (V0.44.1): leave the document — and the dashboard, when the document
              was opened over it — and land on the node this page belongs to. */}
          {ownerNode ? (
            <button
              className="btn"
              id="doc-showmap"
              title={`Show ${ownerNode.label || 'this node'} on the map`}
              onClick={() => callAction('showDocNodeOnMap', ownerNode.id)}
            >
              <svg className="ico">
                <use href="#i-target" />
              </svg>
              <span>Show in map</span>
            </button>
          ) : null}
          {editMode || !s.docCurrent ? null : (
            <button className="btn" id="doc-newtab" onClick={() => window.open(s.docCurrent, '_blank')}>
              <svg className="ico">
                <use href="#i-ext" />
              </svg>
              <span>Open in new tab</span>
            </button>
          )}
          {/* Says where it goes: back to the Documents tab when the dashboard is beneath. */}
          <button className="btn" id="doc-close" onClick={() => callAction('closeDoc', { frame: true })}>
            <svg className="ico">
              <use href="#i-x" />
            </svg>
            <span>{overDocuments ? 'Back to Documents' : 'Close'}</span>
          </button>
        </div>
      </div>

      <div className="docstage" ref={stageRef}>
        <svg
          id="docnav-wires"
          className="docnav-wires"
          width={nav.width || undefined}
          height={nav.height || undefined}
          viewBox={nav.width ? `0 0 ${nav.width} ${nav.height}` : undefined}
        >
          {nav.wires.map((w) => (
            <path
              key={w.key}
              d={w.d}
              fill="none"
              stroke={w.color}
              strokeWidth="2"
              strokeOpacity={s.isLight ? 0.75 : 0.85}
              strokeLinecap="round"
            />
          ))}
        </svg>

        <div
          id="docnav-grid"
          ref={gridRef}
          className={[focus ? '' : 'no-nav', logsVisible ? 'with-logs' : '']
            .filter(Boolean)
            .join(' ')}
          style={
            nav.parentDocClearance === null
              ? undefined
              : ({ '--pdocs-h': `${nav.parentDocClearance}px` } as CSSProperties)
          }
        >
          <div className="docnav-parentgroup" id="docnav-parent">
            {focus && parent ? (
              <>
                <NavNode n={parent} role="parent" ctx={ctx} />
                <DocCol n={parent} cap={PARENT_DOC_CAP} ctx={ctx} />
              </>
            ) : null}
          </div>

          <div className="docnav-currentgroup" id="docnav-current">
            {focus ? (
              <>
                <NavNode n={focus} role="current" ctx={ctx} />
                {currentDocs}
              </>
            ) : null}
          </div>

          <div className="docframe-wrap">
            {editingDoc ? (
              <DocEditorPane
                docId={docId}
                doc={doc}
                message={loadMessage}
                docType={docType}
                onDocTypeChange={setDocType}
                url={url}
                onUrlChange={setUrl}
                editorRef={editorRef}
              />
            ) : null}

            {/* View mode: the button that leaves the app. Same slot for both types, so the
                control does not jump depending on what the page turns out to be. */}
            <div
              id="doc-actions"
              className="doc-actions"
              style={{ display: showActions ? undefined : 'none' }}
            >
              {showActions ? (
                <DocPill
                  variant="lg"
                  icon={type === 'external' ? 'i-ext' : 'i-supademo'}
                  color={docColor}
                  title={s.docCurrent}
                  label={
                    type === 'external'
                      ? `Open ${(docId && s.docTitles[docId]) || s.docCurrentTitle || 'link'}`
                      : 'Open in a new tab'
                  }
                  onClick={() => window.open(s.docCurrent, '_blank', 'noopener')}
                />
              ) : null}
            </div>

            {/* KEYED ON THE URL, and that is history correctness, not a React nicety (plan
                1.6): navigating an EXISTING iframe — which is what swapping `src` is — adds a
                JOINT session-history entry, so every doc open/close would plant an invisible
                entry the Back button has to click through twice. A fresh element's first load
                replaces its initial empty document instead, creating no entry at all. */}
            <iframe
              key={frameSrc}
              id="doc-frame"
              title="Documentation"
              ref={frameRef}
              src={frameSrc}
              onLoad={onFrameLoad}
              style={{ display: showFrame && !failedLoad ? 'block' : 'none' }}
            />

            {/* Shown INSTEAD of the frame: the empty page under an external link's button, or
                the "no documentation" banner when a node has nothing at all. */}
            <div
              id="doc-standin"
              className="doc-standin"
              style={{ display: showStandin ? undefined : 'none' }}
            >
              {noDoc ? (
                <NoDocMessage editMode={editMode} node={focus} offersCreate={bandOffersCreate} />
              ) : null}
            </div>

            <div
              id="doc-error"
              className="doc-error"
              style={{ display: failedLoad && mode === 'view' ? 'flex' : 'none' }}
            >
              {/* An embedded page that never loads is almost always one refusing to be framed
                  (X-Frame-Options / frame-ancestors), which is a different problem from a
                  broken document and has a different answer. */}
              <p id="doc-error-title">
                {type === 'iframe'
                  ? "This page can't be displayed inside InfoStation."
                  : "This page couldn't be shown inside the app."}
              </p>
              <p className="doc-error-sub" id="doc-error-sub">
                {type === 'iframe'
                  ? 'The site does not allow being embedded in another page. Open it in a new tab instead.'
                  : 'This usually happens when opening InfoStation directly from a file. Run it from a local server, or use the button below.'}
              </p>
              <button
                className="pbtn primary"
                id="doc-error-open"
                onClick={() => {
                  if (s.docCurrent) window.open(s.docCurrent, '_blank');
                }}
              >
                Open in a new tab
              </button>
            </div>
          </div>

          <div
            className="docnav-children"
            id="docnav-children"
            style={{ marginTop: nav.childrenOffset ? `${nav.childrenOffset}px` : undefined }}
          >
            {focus
              ? kids.map((id) => (
                  <div className="docnav-childitem" data-child-id={id} key={id}>
                    <NavNode n={s.nodes[id]} role="child" ctx={ctx} />
                    <DocCol n={s.nodes[id]} ctx={ctx} />
                  </div>
                ))
              : null}
          </div>

          {/* The version timeline. It decides for itself whether to be here — edit mode, AI
              admin, and a page open — and says so, because its presence changes this grid. */}
          <DocLogTimeline onVisibleChange={setLogsVisible} />
        </div>
      </div>
    </>
  );
}

/**
 * What a node with no documentation says.
 *
 * Shown in BOTH modes, since edit mode here is how the first page gets added — the wording
 * changes to point at the pills that do it. A node EXEMPT in its documentation plan (V0.42,
 * expectedDocs = {}, "nothing expected here" — the successor to the retired "No doc" tag) is
 * not a node whose documentation is missing; that is a different answer to the same question.
 */
function NoDocMessage({
  editMode,
  node,
  offersCreate,
}: {
  editMode: boolean;
  node: MapNode | null;
  offersCreate: boolean;
}) {
  const exempt = !!node && isExemptPlan(node.expectedDocs);
  if (editMode) {
    // The band draws a pill for each document the PLAN expects and nothing for the rest
    // (V0.42.5), so with no plan there is nothing here to press — and saying otherwise sends
    // people looking for a button that is not there. The node panel is where an unplanned page
    // is attached or created, and where the plan itself is written.
    if (!offersCreate) {
      return (
        <p>
          {exempt
            ? 'This node is marked as needing no documentation. Open its panel on the map to attach a page anyway.'
            : "Nothing here yet, and this node's documentation plan does not name anything. Open its panel on the map to set a plan, or to attach a page."}
        </p>
      );
    }
    return (
      <p>
        {exempt
          ? 'This node is marked as needing no documentation. Use the outlined buttons above to add some anyway.'
          : "Nothing here yet. Use the outlined buttons above to create this node's first documentation."}
      </p>
    );
  }
  return (
    <p>
      {exempt
        ? 'No documentation is needed for this node.'
        : 'Documentation for this node is missing or being reworked.'}
    </p>
  );
}
